import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { Button } from "../../../components/Button";
import { Checkbox } from "../../../components/Checkbox";
import { DateRangePicker } from "../../../components/DateRangePicker";
import { ProgressBar } from "../../../components/ProgressBar";
import { RadioButton } from "../../../components/RadioButton";
import { SelectDropdown } from "../../../components/SelectDropdown";
import { SliderInput } from "../../../components/Slider";
import { TableSorted } from "../../../components/TableSorted";
import { TimePickerInput } from "../../../components/TimePicker";
import { Toggle } from "../../../components/Toggle";
import { horizontalSliderCss } from "../../Surveys/SurveyBuilder/Components/ContentTab/Components/SliderQuestionDisplay/SliderQuestionDisplay.styles";
import {
  PreviewBuilderAction,
  PreviewBuilderBanner,
  PreviewBuilderButton,
  PreviewBuilderTab,
  PreviewButtonState,
  PreviewCaption,
  PreviewCell,
  PreviewEditableField,
  PreviewFilterChip,
  PreviewFrame,
  PreviewGhostButton,
  PreviewInputBox,
  PreviewMessagesCircle,
  PreviewNavbar,
  PreviewNavButton,
  PreviewNavLink,
  PreviewNavSearch,
  PreviewNote,
  PreviewOkButton,
  PreviewPopupIcon,
  PreviewPopupOption,
  PreviewRow,
  PreviewRowLabel,
  PreviewSection,
  PreviewSectionLabel,
  PreviewStack,
  PreviewTable,
  PreviewTableRow,
  PreviewToolbar,
  PreviewToolbarIcon,
  PreviewWardIcon,
  PreviewWardOption,
  PreviewWardTrigger,
} from "./TenantStyles.styles";

/**
 * Live samples for the tenant theme editor (EN-5154).
 *
 * Real portal components are used wherever one shows a token **at rest** —
 * they render inside a scoped tenant theme (`useTenantThemeClass`), so they
 * are the real thing under the tenant's colours and can't drift.
 *
 * Tokens that only appear on hover, focus, selection or in an open popup get
 * a stand-in with that state **pinned** and labelled. A real component shows
 * nothing for those at rest, which is why the first cut of this editor left
 * 81 of the 95 colours with no visible effect at all.
 *
 * Every token must appear in some sample — `TenantStylePreviews.coverage.test`
 * fails if one doesn't.
 */

interface IPreviewRow {
  id: number;
  name: string;
  ward: string;
}

const PREVIEW_ROWS: IPreviewRow[] = [
  { id: 1, name: "Ava Mitchell", ward: "Respiratory" },
  { id: 2, name: "Liam Carter", ward: "Cardiology" },
  { id: 3, name: "Sophia Bennett", ward: "Transplant" },
];

const DROPDOWN_OPTIONS = [
  { key: "resp", value: "resp", label: "Respiratory" },
  { key: "card", value: "card", label: "Cardiology" },
];

export function NavbarPreview() {
  return (
    <PreviewFrame>
      <PreviewNavbar>
        <PreviewNavLink state="selected">Patients</PreviewNavLink>
        <PreviewNavLink state="hover">Reports</PreviewNavLink>
        <PreviewNavLink state="focusSelected">Settings</PreviewNavLink>
        <PreviewNavSearch>Search</PreviewNavSearch>
        <PreviewNavButton>Action</PreviewNavButton>
      </PreviewNavbar>

      <PreviewSection>
        <PreviewSectionLabel>Ward select</PreviewSectionLabel>
        <PreviewStack>
          <PreviewWardTrigger>
            <PreviewWardIcon>▾</PreviewWardIcon> Ward A
          </PreviewWardTrigger>
          <PreviewWardTrigger state="hover">
            <PreviewWardIcon>▾</PreviewWardIcon> Hover
          </PreviewWardTrigger>
          <PreviewWardOption state="selected">Selected</PreviewWardOption>
          <PreviewWardOption state="hover">Option hover</PreviewWardOption>
        </PreviewStack>
      </PreviewSection>

      <PreviewCaption>
        States are pinned — the real navbar renders inside the app shell.
      </PreviewCaption>
    </PreviewFrame>
  );
}

export function TablePreview() {
  const columns: ColumnDef<IPreviewRow, string>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Patient",
        cell: ({ row }) => <span>{row.original.name}</span>,
      },
      {
        accessorKey: "ward",
        header: "Ward",
        cell: ({ row }) => <span>{row.original.ward}</span>,
      },
    ],
    []
  );

  return (
    <PreviewFrame>
      <TableSorted
        columns={columns}
        data={PREVIEW_ROWS}
        onRowClick={() => {}}
        emptyStateText="No rows"
      />

      <PreviewSection>
        <PreviewSectionLabel>Row states</PreviewSectionLabel>
        <PreviewTable>
          <PreviewTableRow state="hover">
            Hover
            <PreviewMessagesCircle />
          </PreviewTableRow>
          <PreviewTableRow state="selected">Selected</PreviewTableRow>
          <PreviewTableRow state="focus">Focus</PreviewTableRow>
          <PreviewTableRow state="highlightedPrimary">
            Highlighted
          </PreviewTableRow>
          <PreviewTableRow state="highlightedSecondary">
            Highlighted (alternate)
          </PreviewTableRow>
        </PreviewTable>
      </PreviewSection>
    </PreviewFrame>
  );
}

export function ButtonsPreview() {
  return (
    <PreviewFrame>
      <PreviewStack>
        <Button type="button" label="Primary" onClick={() => {}} />
        <Button type="button" label="Loading" isLoading onClick={() => {}} />
        <Button type="button" label="Disabled" disabled onClick={() => {}} />
      </PreviewStack>

      <PreviewSection>
        <PreviewSectionLabel>Pinned states</PreviewSectionLabel>
        <PreviewStack>
          <PreviewButtonState state="hover">Hover</PreviewButtonState>
          <PreviewButtonState state="focus">Focus</PreviewButtonState>
          <PreviewButtonState state="active">Active</PreviewButtonState>
          <PreviewButtonState state="loadingHover">
            Loading hover
          </PreviewButtonState>
        </PreviewStack>
      </PreviewSection>
    </PreviewFrame>
  );
}

export function BuilderPreview() {
  return (
    <PreviewFrame>
      <PreviewBuilderBanner>
        <PreviewStack>
          <PreviewBuilderTab state="selected">Content</PreviewBuilderTab>
          <PreviewBuilderTab state="hover">Rules</PreviewBuilderTab>
          <PreviewBuilderTab state="focus">Preview</PreviewBuilderTab>
        </PreviewStack>
        <PreviewStack>
          <PreviewBuilderButton>Primary</PreviewBuilderButton>
          <PreviewBuilderButton variant="primaryHover">
            Hover
          </PreviewBuilderButton>
          <PreviewBuilderButton variant="primaryFocus">
            Focus
          </PreviewBuilderButton>
        </PreviewStack>
        <PreviewStack>
          <PreviewBuilderButton variant="secondary">
            Secondary
          </PreviewBuilderButton>
          <PreviewBuilderButton variant="secondaryHover">
            Hover
          </PreviewBuilderButton>
          <PreviewBuilderButton variant="secondaryFocus">
            Focus
          </PreviewBuilderButton>
        </PreviewStack>
        <PreviewStack>
          <PreviewBuilderButton variant="tertiary">
            Tertiary
          </PreviewBuilderButton>
          <PreviewBuilderButton variant="tertiaryHover">
            Hover
          </PreviewBuilderButton>
          <PreviewBuilderButton variant="tertiaryFocus">
            Focus
          </PreviewBuilderButton>
        </PreviewStack>
      </PreviewBuilderBanner>

      <PreviewBuilderAction>Active protocol action</PreviewBuilderAction>

      <PreviewCaption>
        States are pinned — the builder renders inside its own layout.
      </PreviewCaption>
    </PreviewFrame>
  );
}

export function MeasurementModalPreview() {
  return (
    <PreviewFrame>
      <PreviewStack>
        <PreviewFilterChip>Last 7 days</PreviewFilterChip>
        <PreviewFilterChip state="active">Last 30 days</PreviewFilterChip>
        <PreviewFilterChip state="activeHover">Hover</PreviewFilterChip>
      </PreviewStack>
      <PreviewStack>
        <PreviewGhostButton tone="baselineSave">
          Save baseline
        </PreviewGhostButton>
        <PreviewGhostButton tone="confirmCancelHover">
          Cancel (hover)
        </PreviewGhostButton>
      </PreviewStack>
      <PreviewCaption>
        States are pinned — these live inside the measurement modal.
      </PreviewCaption>
    </PreviewFrame>
  );
}

export function PickersPreview() {
  const [time, setTime] = useState(dayjs());

  return (
    <PreviewFrame>
      <DateRangePicker
        initialDateRange={{ startDate: new Date(), endDate: null }}
      />

      <PreviewRow>
        <PreviewRowLabel>Time</PreviewRowLabel>
        <TimePickerInput value={time} onChange={(next) => setTime(next)} />
      </PreviewRow>

      <PreviewSection>
        <PreviewSectionLabel>Popup & pinned states</PreviewSectionLabel>
        <PreviewStack>
          <PreviewCell state="today">12</PreviewCell>
          <PreviewCell state="calendarSelected">13</PreviewCell>
          <PreviewCell state="timeSelected">09:00</PreviewCell>
          <PreviewCell state="timePopupSelected">10:00</PreviewCell>
          <PreviewOkButton>OK</PreviewOkButton>
          <PreviewOkButton state="hover">OK (hover)</PreviewOkButton>
        </PreviewStack>
        <PreviewStack>
          <PreviewInputBox state="timeHover">Time hover</PreviewInputBox>
          <PreviewInputBox state="timeFocus">Time focus</PreviewInputBox>
        </PreviewStack>
      </PreviewSection>

      <PreviewSection>
        <PreviewSectionLabel>Date range</PreviewSectionLabel>
        <PreviewStack>
          <PreviewCell state="rangeToday">Today</PreviewCell>
          <PreviewCell state="rangeSelected">Selected</PreviewCell>
          <PreviewCell state="rangeTrail">In range</PreviewCell>
          <PreviewCell state="rangeHover">Hover</PreviewCell>
        </PreviewStack>
      </PreviewSection>
    </PreviewFrame>
  );
}

export function InputsPreview() {
  const [checked, setChecked] = useState(true);
  const [toggled, setToggled] = useState(true);
  const [selected, setSelected] = useState(true);
  const [ward, setWard] = useState<string | string[]>("resp");

  return (
    <PreviewFrame>
      <PreviewRow>
        <PreviewRowLabel>Toggle</PreviewRowLabel>
        <PreviewStack>
          <Toggle checked={toggled} onCheckedChange={setToggled} />
        </PreviewStack>
      </PreviewRow>

      <PreviewRow>
        <PreviewRowLabel>Checkbox</PreviewRowLabel>
        <PreviewStack>
          <Checkbox
            label="Receives alerts"
            checked={checked}
            onChange={setChecked}
          />
        </PreviewStack>
      </PreviewRow>

      <PreviewRow>
        <PreviewRowLabel>Radio</PreviewRowLabel>
        <PreviewStack>
          <RadioButton
            checked={selected}
            onClick={() => setSelected(!selected)}
          />
          <RadioButton
            checked={!selected}
            onClick={() => setSelected(!selected)}
          />
        </PreviewStack>
      </PreviewRow>

      <PreviewRow>
        <PreviewRowLabel>Dropdown</PreviewRowLabel>
        <SelectDropdown
          options={DROPDOWN_OPTIONS}
          value={ward}
          onValueChange={setWard}
          placeholder="Select a ward"
        />
      </PreviewRow>

      <PreviewSection>
        <PreviewSectionLabel>Pinned states</PreviewSectionLabel>
        <PreviewStack>
          <PreviewInputBox state="textHover">Input hover</PreviewInputBox>
          <PreviewInputBox state="textFocus">Input focus</PreviewInputBox>
          <PreviewInputBox state="dropdownSelected">Selected</PreviewInputBox>
          <PreviewPopupOption>
            <PreviewPopupIcon>✓</PreviewPopupIcon> Selected option
          </PreviewPopupOption>
        </PreviewStack>
      </PreviewSection>
    </PreviewFrame>
  );
}

export function SurveysPreview() {
  const [answer, setAnswer] = useState(6);

  return (
    <PreviewFrame>
      <PreviewRow>
        <PreviewRowLabel>Progress</PreviewRowLabel>
        <ProgressBar percent={60} />
      </PreviewRow>

      {/* The real survey slider, wearing the same css helper the survey
          builder gives it — that helper is where the slider tokens live. */}
      <PreviewRow>
        <PreviewRowLabel>Slider</PreviewRowLabel>
        <SliderInput
          className={horizontalSliderCss()}
          min={0}
          max={10}
          value={answer}
          valueVisible
          onValueChange={setAnswer}
        />
      </PreviewRow>
    </PreviewFrame>
  );
}

export function MiscPreview() {
  return (
    <PreviewFrame>
      <PreviewNote>Note shown inside a portal modal.</PreviewNote>

      <PreviewToolbar>
        <PreviewToolbarIcon state="active">B</PreviewToolbarIcon>
        <PreviewToolbarIcon state="focus">I</PreviewToolbarIcon>
        <PreviewToolbarIcon>U</PreviewToolbarIcon>
      </PreviewToolbar>

      <PreviewStack>
        <PreviewGhostButton tone="careInfo">+ Add link</PreviewGhostButton>
        <PreviewGhostButton tone="careInfoHover">
          + Add link (hover)
        </PreviewGhostButton>
        <PreviewGhostButton tone="loading">Loading…</PreviewGhostButton>
      </PreviewStack>

      <PreviewSection>
        <PreviewSectionLabel>Patient details</PreviewSectionLabel>
        <PreviewStack>
          <PreviewEditableField state="hover">Field hover</PreviewEditableField>
          <PreviewEditableField state="dropdownHover">
            Dropdown hover
          </PreviewEditableField>
          <PreviewEditableField state="addHover">+ Add</PreviewEditableField>
        </PreviewStack>
      </PreviewSection>

      <PreviewCaption>
        States are pinned — these belong to page-specific chrome.
      </PreviewCaption>
    </PreviewFrame>
  );
}

/** Group id → sample. Every group has one. */
export const STYLE_GROUP_PREVIEWS: Record<string, () => JSX.Element> = {
  navbar: NavbarPreview,
  table: TablePreview,
  buttons: ButtonsPreview,
  builder: BuilderPreview,
  measurementModal: MeasurementModalPreview,
  pickers: PickersPreview,
  inputs: InputsPreview,
  surveys: SurveysPreview,
  misc: MiscPreview,
};
